// src/context/InvoicesContext.jsx
import { createContext, useContext, useState, useEffect } from "react";
import { API_URL } from "../config";

const InvoicesContext = createContext();

export const useInvoices = () => useContext(InvoicesContext);

export const InvoicesProvider = ({ children }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Backend URL — works locally & in production
  // const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Fetch all invoices for logged-in user (via session cookie)
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/invoices/get`, {
        credentials: "include", // Sends session cookie
      });

      const data = await res.json();

      if (data.success) {
        setInvoices(data.data || []);
      } else {
        console.warn("Failed to fetch invoices:", data.error);
        setInvoices([]);
      }
    } catch (err) {
      console.error("Network error fetching invoices:", err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  // Save or update invoice
  const addInvoice = async (invoice) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(invoice),
      });

      const data = await res.json();

      if (data.success) {
        setInvoices((prev) => [...prev, data.data]);
        return data.data;
      } else {
        console.error("Save failed:", data.error);
        alert("Failed to save invoice: " + (data.error || "Unknown error"));
      }
    } catch (err) {
      console.error("Network error saving invoice:", err);
      alert("Network error. Please try again.");
    }
  };

  // Update existing invoice
  const updateInvoice = async (id, updated) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...updated, _id: id }),
      });

      const data = await res.json();

      if (data.success) {
        setInvoices((prev) =>
          prev.map((inv) => (inv._id === id ? data.data : inv))
        );
        return data.data;
      }
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  // Delete invoice
  const deleteInvoice = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/delete/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();

      if (data.success) {
        setInvoices((prev) => prev.filter((inv) => inv._id !== id));
      } else {
        console.error("Delete failed:", data.error);
      }
    } catch (err) {
      console.error("Delete network error:", err);
    }
  };

  // Auto-fetch on mount (after login sync sets session)
  useEffect(() => {
    fetchInvoices();
  }, []);

  // Optional: Refetch if another tab logs in
  useEffect(() => {
    const handleStorage = () => {
      if (localStorage.getItem("userId") && invoices.length === 0) {
        fetchInvoices();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [invoices.length]);

  return (
    <InvoicesContext.Provider
      value={{
        invoices,
        loading,
        addInvoice,
        updateInvoice,
        deleteInvoice,
        refetch: fetchInvoices,
      }}
    >
      {children}
    </InvoicesContext.Provider>
  );
};
